import { createContext, useContext, useState, ReactNode, useCallback } from 'react';

interface QueryCache {
  [key: string]: {
    data: any;
    timestamp: number;
    expiresIn: number;
  };
}

interface DataCacheContextType {
  getCachedData: (key: string) => any;
  setCachedData: (key: string, data: any, expiresIn?: number) => void;
  invalidateCache: (pattern?: string) => void;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

const DEFAULT_CACHE_TIME = 5 * 60 * 1000; // 5 minutes

export function DataCacheProvider({ children }: { children: ReactNode }) {
  const [cache, setCache] = useState<QueryCache>({});

  const getCachedData = useCallback((key: string) => {
    const cached = cache[key];
    if (!cached) return null;
    
    const now = Date.now();
    if (now > cached.timestamp + cached.expiresIn) {
      // Cache expired, remove it
      setCache(prev => {
        const next = { ...prev };
        delete next[key];
        return next;
      });
      return null;
    }
    
    return cached.data;
  }, [cache]);

  const setCachedData = useCallback((key: string, data: any, expiresIn = DEFAULT_CACHE_TIME) => {
    setCache(prev => ({
      ...prev,
      [key]: {
        data,
        timestamp: Date.now(),
        expiresIn
      }
    }));
  }, []);

  const invalidateCache = useCallback((pattern?: string) => {
    if (!pattern) {
      setCache({});
      return;
    }
    
    setCache(prev => {
      const next = { ...prev };
      Object.keys(next).forEach(key => {
        if (key.includes(pattern)) {
          delete next[key];
        }
      });
      return next;
    });
  }, []);

  return (
    <DataCacheContext.Provider value={{ getCachedData, setCachedData, invalidateCache }}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (!context) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
}